import { useCallback, useRef, useEffect } from 'react';

/**
 * Hook for persisting form data to localStorage with debouncing
 * @param {string} storageKey - The key to use in localStorage
 * @param {boolean} isEnabled - Whether persistence is enabled (default: true)
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 1500)
 * @returns {object} - Methods for saving, loading, and clearing storage
 */
const useFormPersistence = (storageKey, isEnabled = true, debounceMs = 1500) => {
  const timeoutRef = useRef(null);

  /**
   * Save data to localStorage with debouncing
   */
  const saveToStorage = useCallback(
    (data) => {
      if (!isEnabled) return;

      // Clear existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout for debounced save
      timeoutRef.current = setTimeout(() => {
        try {
          const serializedData = JSON.stringify(data);
          localStorage.setItem(storageKey, serializedData);
          console.log(`Form data auto-saved to localStorage: ${storageKey}`);
        } catch (error) {
          console.error('Error saving to localStorage:', error);
        }
      }, debounceMs);
    },
    [storageKey, isEnabled, debounceMs]
  );

  /**
   * Load data from localStorage
   */
  const loadFromStorage = useCallback(() => {
    if (!isEnabled) return null;

    try {
      const serializedData = localStorage.getItem(storageKey);
      if (serializedData === null) {
        return null;
      }
      const data = JSON.parse(serializedData);
      console.log(`Form data loaded from localStorage: ${storageKey}`);
      return data;
    } catch (error) {
      console.error('Error loading from localStorage:', error);
      return null;
    }
  }, [storageKey, isEnabled]);

  /**
   * Clear data from localStorage
   */
  const clearStorage = useCallback(() => {
    try {
      localStorage.removeItem(storageKey);
      console.log(`Form data cleared from localStorage: ${storageKey}`);
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }, [storageKey]);

  /**
   * Check if data exists in localStorage
   * @param {function} validator - Optional validator function to check if data is meaningful
   */
  const hasStoredData = useCallback((validator) => {
    if (!isEnabled) return false;

    try {
      const serializedData = localStorage.getItem(storageKey);
      if (!serializedData) return false;

      // If validator provided, use it to check if data is meaningful
      if (validator && typeof validator === 'function') {
        const data = JSON.parse(serializedData);
        return validator(data);
      }

      return true;
    } catch (error) {
      console.error('Error checking localStorage:', error);
      return false;
    }
  }, [storageKey, isEnabled]);

  /**
   * Cleanup timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    saveToStorage,
    loadFromStorage,
    clearStorage,
    hasStoredData
  };
};

export default useFormPersistence;
